import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { summarizeFbReportingData, formatOptimizerCurrency } from '../utils/optimizerResultPresentation';
import './ReportingDataVisualization.css';

/**
 * ReportingDataVisualization - Component to visualize campaign reporting data
 * Displays campaign, ad group, and ad-level metrics in a structured format
 * Supports:
 * - Reddit format (campaign_report, ad_group_reports, ad_reports)
 * - Facebook format (insights object)
 * - YouTube DV360 format (Summary, Performance By Date)
 */
const ReportingDataVisualization = ({ reportingData, currency = null }) => {
  if (!reportingData) {
    return null;
  }

  // Account billing currency for money labels. Prefer the explicit prop (resolved
  // by the optimizer from customer.currency_code), then any account_currency the
  // payload carries. Null → formatOptimizerCurrency falls back to legacy "$".
  const accountCurrency = currency || reportingData.account_currency || null;

  // Detect format
  const isFacebookFormat = reportingData.insights && !reportingData.campaign_report;
  const isYouTubeDV360Format = reportingData.Summary && reportingData['Performance By Date'];

  const { campaign_report, ad_group_reports, ad_reports, summary, date_preset } = reportingData;

  // Format currency in the account's real currency (not a hardcoded "$"). Values
  // are in micro-currency units for Reddit/Google Ads; Facebook passes regular units.
  const formatCurrency = (value, isMicroUnits = true) => {
    if (!value && value !== 0) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    const amount = isMicroUnits ? numValue / 1000000 : numValue;
    return formatOptimizerCurrency(amount, accountCurrency);
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value && value !== 0) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return `${numValue.toFixed(2)}%`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return numValue.toLocaleString();
  };

  // Render metrics table
  const renderMetricsTable = (metrics, type) => {
    if (!metrics || metrics.length === 0) {
      return <p style={{ color: '#666', fontStyle: 'italic' }}>No data available</p>;
    }

    return (
      <div className="metrics-table-container">
        <table className="metrics-table">
          <thead>
            <tr>
              {type === 'ad' && <th>Ad ID</th>}
              {type === 'adGroup' && <th>Ad Group ID</th>}
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Spend</th>
              <th>CPC</th>
              {type === 'adGroup' && <th>Pacing</th>}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, index) => (
              <tr key={index}>
                {type === 'ad' && <td className="id-cell">{metric.ad_id}</td>}
                {type === 'adGroup' && <td className="id-cell">{metric.ad_group_id}</td>}
                <td className="number-cell">{formatNumber(metric.impressions)}</td>
                <td className="number-cell">{formatNumber(metric.clicks)}</td>
                <td className="percentage-cell">{formatPercentage(metric.ctr)}</td>
                <td className="currency-cell">{formatCurrency(metric.spend)}</td>
                <td className="currency-cell">{formatCurrency(metric.cpc)}</td>
                {type === 'adGroup' && (
                  <td className="percentage-cell">
                    {metric.pacing ? `${metric.pacing.toFixed(2)}%` : 'N/A'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Facebook insights format. `insights` is an ARRAY of per-ad rows
  // (pull_campaign_report level='ad'); older payloads may carry one object.
  // summarizeFbReportingData normalizes both and computes campaign totals;
  // money renders in the ad account currency (reportingData.account_currency).
  const renderFacebookInsights = () => {
    const summary = summarizeFbReportingData(reportingData);
    if (!summary) {
      console.log('❌ No Facebook insights found');
      return <p style={{ color: '#666', fontStyle: 'italic' }}>No insights data available</p>;
    }

    const { rows, totals, currency } = summary;
    const money = (v) => formatOptimizerCurrency(v, currency);
    const multiRow = rows.length > 1;

    return (
      <div className="metrics-summary-table">
        <table>
          <thead>
            <tr>
              {multiRow && <th>Ad</th>}
              <th>Impressions</th>
              <th>Reach</th>
              <th>Clicks</th>
              <th>Unique Clicks</th>
              <th>CTR</th>
              <th>Spend</th>
              <th>Frequency</th>
              <th>Cost/Click</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {multiRow && <td className="id-cell">{row.ad_id || row.adset_id || '—'}</td>}
                <td>{formatNumber(row.impressions)}</td>
                <td>{formatNumber(row.reach)}</td>
                <td>{formatNumber(row.clicks)}</td>
                <td>{formatNumber(row.unique_clicks)}</td>
                <td>{formatPercentage(row.ctr)}</td>
                <td>{money(row.spend)}</td>
                <td>{row.frequency ? parseFloat(row.frequency).toFixed(2) : 'N/A'}</td>
                <td>{money(row.cost_per_unique_click)}</td>
              </tr>
            ))}
            {multiRow && (
              <tr style={{ fontWeight: 600, borderTop: '2px solid #ccc' }}>
                <td>Total</td>
                <td>{formatNumber(totals.impressions)}</td>
                {/* Per-ad reach overlaps — a sum would double-count people. */}
                <td>—</td>
                <td>{formatNumber(totals.clicks)}</td>
                <td>{formatNumber(totals.unique_clicks)}</td>
                <td>{formatPercentage(totals.ctr)}</td>
                <td>{money(totals.spend)}</td>
                <td>—</td>
                <td>{money(totals.cpc)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Aggregate daily ad reports data (Google Ads Search format)
  const aggregateAdReports = (adReports) => {
    if (!adReports?.report_data?.data || adReports.report_data.data.length === 0) {
      return null;
    }

    const data = adReports.report_data.data;
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalCostMicros = 0;
    let totalConversions = 0;

    data.forEach(row => {
      totalImpressions += row.impressions || 0;
      totalClicks += row.clicks || 0;
      totalCostMicros += row.cost_micros || 0;
      totalConversions += row.conversions || 0;
    });

    const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) : 0;
    const avgCPC = totalClicks > 0 ? (totalCostMicros / totalClicks) : 0;

    return {
      impressions: totalImpressions,
      clicks: totalClicks,
      ctr: avgCTR,
      spend: totalCostMicros,
      cpc: avgCPC,
      conversions: totalConversions
    };
  };

  // Aggregate daily data by date for trend visualization
  const aggregateDailyTrends = (adReports) => {
    if (!adReports?.report_data?.data || adReports.report_data.data.length === 0) {
      return [];
    }

    const data = adReports.report_data.data;
    const dailyMap = {};

    data.forEach(row => {
      const date = row.date;
      if (!dailyMap[date]) {
        dailyMap[date] = {
          date,
          impressions: 0,
          clicks: 0,
          cost_micros: 0,
          conversions: 0
        };
      }
      dailyMap[date].impressions += row.impressions || 0;
      dailyMap[date].clicks += row.clicks || 0;
      dailyMap[date].cost_micros += row.cost_micros || 0;
      dailyMap[date].conversions += row.conversions || 0;
    });

    return Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Render campaign summary metrics (Reddit format or aggregated Google Ads Search)
  const renderCampaignSummary = () => {
    let metrics;

    // Check for Google Ads Search daily ad reports format
    if (ad_reports?.report_data?.data && Array.isArray(ad_reports.report_data.data)) {
      metrics = aggregateAdReports(ad_reports);
      if (!metrics) {
        console.log('❌ No ad reports data to aggregate');
        return <p style={{ color: '#666', fontStyle: 'italic' }}>No campaign data available</p>;
      }
      console.log('📊 Aggregated Google Ads Metrics:', metrics);
    }
    // Check for Reddit format
    else if (campaign_report?.report_data?.data?.metrics?.[0]) {
      metrics = campaign_report.report_data.data.metrics[0];
      console.log('📊 Reddit Campaign Metrics:', metrics);
    }
    else {
      console.log('❌ No campaign metrics found');
      return <p style={{ color: '#666', fontStyle: 'italic' }}>No campaign data available</p>;
    }

    return (
      <div className="metrics-summary-table">
        <table>
          <thead>
            <tr>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Total Spend</th>
              <th>Avg CPC</th>
              <th>Conversions</th>
              <th>Cost / Conv.</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{formatNumber(metrics.impressions)}</td>
              <td>{formatNumber(metrics.clicks)}</td>
              <td>{formatPercentage(metrics.ctr * 100)}</td>
              <td>{formatCurrency(metrics.spend)}</td>
              <td>{formatCurrency(metrics.cpc)}</td>
              <td>{metrics.conversions != null ? formatNumber(metrics.conversions) : 'N/A'}</td>
              <td>{metrics.conversions > 0 ? formatCurrency(metrics.spend / metrics.conversions) : 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render daily trend visualization
  const renderDailyTrends = () => {
    let dailyData;

    // Check for Google Ads Search daily ad reports format
    if (ad_reports?.report_data?.data && Array.isArray(ad_reports.report_data.data)) {
      dailyData = aggregateDailyTrends(ad_reports);
    } else {
      return null; // No daily data available
    }

    if (!dailyData || dailyData.length === 0) {
      return null;
    }

    console.log('📈 Daily Trends:', dailyData);

    // Find max values for scaling
    const maxImpressions = Math.max(...dailyData.map(d => d.impressions));

    return (
      <div style={{ marginTop: '20px' }}>
        <div className="section-header-static">
          <h5>📈 Daily Performance Trends</h5>
        </div>
        <div style={{ padding: '16px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Impressions</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Clicks</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>CTR</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Spend</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>CPC</th>
                  <th style={{ padding: '10px', width: '200px', fontWeight: '600', color: '#495057' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {dailyData.map((day, index) => {
                  const ctr = day.impressions > 0 ? (day.clicks / day.impressions) : 0;
                  const cpc = day.clicks > 0 ? (day.cost_micros / day.clicks) : 0;
                  const impressionBarWidth = maxImpressions > 0 ? (day.impressions / maxImpressions * 100) : 0;

                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td style={{ padding: '10px', color: '#212529', fontWeight: '500' }}>{day.date}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#212529' }}>{formatNumber(day.impressions)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#212529' }}>{formatNumber(day.clicks)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#212529' }}>{formatPercentage(ctr * 100)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#212529' }}>{formatCurrency(day.cost_micros)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#212529' }}>{formatCurrency(cpc)}</td>
                      <td style={{ padding: '10px' }}>
                        <div style={{
                          width: '100%',
                          height: '20px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${impressionBarWidth}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render YouTube DV360 format
  if (isYouTubeDV360Format) {
    const summaryData = reportingData.Summary;
    const performanceByDate = reportingData['Performance By Date'];

    return (
      <div className="reporting-data-visualization">
        <div className="reporting-header">
          <h4>
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />
            YouTube DV360 Performance Report
          </h4>
        </div>

        {/* Summary Metrics */}
        <div className="report-section">
          <div className="section-header-static">
            <h5>Campaign Summary</h5>
          </div>
          <div className="section-content">
            <div className="metrics-summary-table">
              <table>
                <thead>
                  <tr>
                    <th>Total Impressions</th>
                    <th>Total Clicks</th>
                    <th>Total Conversions</th>
                    <th>CTR</th>
                    <th>Conversion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatNumber(summaryData['Total Impressions'])}</td>
                    <td>{formatNumber(summaryData['Total Clicks'])}</td>
                    <td>{summaryData['Total Conversions']}</td>
                    <td>{summaryData['Total CTR']}</td>
                    <td>{summaryData['Total Conversion Rate']}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Daily Performance Trends */}
        {performanceByDate && performanceByDate.length > 0 && (
          <div className="report-section">
            <div className="section-header-static">
              <h5>Daily Performance Trends</h5>
            </div>
            <div className="section-content">
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9em',
                  backgroundColor: '#ffffff'
                }}>
                  <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <tr>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Line Item</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Date</th>
                      <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Impressions</th>
                      <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Clicks</th>
                      <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Conversions</th>
                      <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>CTR</th>
                      <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceByDate.map((day, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '10px', color: '#495057' }}>{day.line_item}</td>
                        <td style={{ padding: '10px', color: '#495057' }}>{day.date}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#495057' }}>{formatNumber(day.impressions)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#495057' }}>{formatNumber(day.clicks)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#495057' }}>{day.conversions}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#28a745' }}>{day.ctr}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#28a745' }}>{day.conversion_rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render Facebook format
  if (isFacebookFormat) {
    return (
      <div className="reporting-data-visualization">
        <div className="reporting-header">
          <h4>
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />
            Campaign Performance Report
          </h4>
          {reportingData.status && (
            <span className={`date-badge ${reportingData.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
              {reportingData.status}
            </span>
          )}
        </div>

        {/* Campaign Info */}
        {reportingData.name && (
          <div className="campaign-info-section">
            <div className="info-row">
              <span className="info-label">Campaign:</span>
              <span className="info-value">{reportingData.name}</span>
            </div>
            {reportingData.id && (
              <div className="info-row">
                <span className="info-label">Campaign ID:</span>
                <span className="info-value">{reportingData.id}</span>
              </div>
            )}
            {reportingData.objective && (
              <div className="info-row">
                <span className="info-label">Objective:</span>
                <span className="info-value">{reportingData.objective.replace(/_/g, ' ')}</span>
              </div>
            )}
            {reportingData.insights?.date_start && reportingData.insights?.date_stop && (
              <div className="info-row">
                <span className="info-label">Date Range:</span>
                <span className="info-value">
                  {reportingData.insights.date_start} to {reportingData.insights.date_stop}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Facebook Insights */}
        <div className="report-section">
          <div className="section-header-static">
            <h5>Campaign Metrics</h5>
          </div>
          <div className="section-content">
            {renderFacebookInsights()}
          </div>
        </div>
      </div>
    );
  }

  // Render Reddit format
  return (
    <div className="reporting-data-visualization">
      <div className="reporting-header">
        <h4>
          <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />
          Campaign Performance Report
        </h4>
        {date_preset && (
          <span className="date-badge">{date_preset.toUpperCase()}</span>
        )}
      </div>

      {/* Summary Status - Only show errors */}
      {summary && summary.has_errors && (
        <div className="report-summary">
          <div className="summary-error">
            ⚠️ Report generated with {summary.total_errors} error(s)
          </div>
        </div>
      )}

      {/* Campaign Level Metrics */}
      {(campaign_report?.success || ad_reports?.report_data?.data) && (
        <div className="report-section">
          <div className="section-header-static">
            <h5>Campaign Overview</h5>
          </div>
          <div className="section-content">
            {renderCampaignSummary()}
          </div>
        </div>
      )}

      {/* Daily Performance Trends - for Google Ads Search */}
      {ad_reports?.report_data?.data && Array.isArray(ad_reports.report_data.data) && (
        <div className="report-section">
          {renderDailyTrends()}
        </div>
      )}

      {/* Ad Group Level Metrics */}
      {ad_group_reports?.success && ad_group_reports.report_data?.data?.metrics && (
        <div className="report-section">
          <div className="section-header-static">
            <h5>Ad Group Performance ({ad_group_reports.report_data.data.metrics.length})</h5>
          </div>
          <div className="section-content">
            {renderMetricsTable(ad_group_reports.report_data.data.metrics, 'adGroup')}
          </div>
        </div>
      )}

      {/* Ad Level Metrics */}
      {ad_reports?.success && ad_reports.report_data?.data?.metrics && (
        <div className="report-section">
          <div className="section-header-static">
            <h5>Ad Performance ({ad_reports.report_data.data.metrics.length})</h5>
          </div>
          <div className="section-content">
            {renderMetricsTable(ad_reports.report_data.data.metrics, 'ad')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportingDataVisualization;

